import { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Button, Form, Space, Image, message, Typography, Tag, Row, Col, Popover, Popconfirm, Alert } from 'antd';
import copy from 'copy-to-clipboard';
import './index.less';
import config from '@config';
import { organizationsInfoApi, organizationsReviewedApi, organizationsRejectedApi, cancelMergedOrganizationsApi } from '@/services/DC/Organization';
import { orgTypeList } from '@/utils/constants';
import { logOrganizationApprove, logOrganizationReject, logOrganizationUnmerge } from '@/utils/organizationLogger';
import { ReviewStatusMap } from '@/utils/utils';
import OperationLogDrawer from '@/components/common/OperationLogDrawer';
import { prepareBCOSSUrl, formatEstablishedTime } from '@/utils/utils';

const { Text, Link } = Typography;

// 根据 creditCode 获取背景色的函数
// 算法：使用字符串哈希算法为不同登记号分配固定颜色，实现视觉分组效果
const getCreditCodeBackgroundColor = creditCode => {
    if (!creditCode) return '';

    const colors = ['#fff7e6', '#fff1f0', '#fff2e8', '#fffbe6', '#fcffe6', '#e6fffb', '#e6f7ff', '#f9f0ff'];

    // 哈希算法：hash = hash * 31 + charCode (使用位运算优化)
    let hash = 0;
    for (let i = 0; i < creditCode.length; i++) {
        const char = creditCode.charCodeAt(i);
        hash = (hash << 5) - hash + char; // 等价于 hash * 31 + char
        hash = hash & hash; // 转换为32位整数
    }

    // 取模映射到颜色索引，确保相同creditCode获得相同颜色
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
};

// 查看机构
// 机构类型（orgType）：0未分类，1医疗机构，2政府，3学协会，4科研院校，5企业
const OrganizationView = props => {
    const { openViewOrgDrawer, setOpenViewOrgDrawer, id, setCurrentRecordId, setOpenAddOrgDrawer, showEditButton = true, getOrganizationsList } = props;

    const [openRecordDrawer, setOpenRecordDrawer] = useState(false);
    const [reviewStatus, setReviewStatus] = useState(null);
    const [info, setInfo] = useState({});
    const [branchList, setBranchList] = useState([]);
    const [mergedList, setMergedList] = useState([]);
    const [affiliatedList, setAffiliatedList] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [isSwitchStatus, setIsSwitchStatus] = useState(false);
    const [isIntroductionExpanded, setIsIntroductionExpanded] = useState(false);
    const [shouldShowToggle, setShouldShowToggle] = useState(false);

    const introductionRef = useRef(null);

    const [orgType, setOrgType] = useState(0);
    const [showAllMergeList, setShowAllMergeList] = useState(false); // 展开、收起合并信息

    const openRecord = () => {
        setOpenRecordDrawer(true);
    };

    const onClose = () => {
        setOpenViewOrgDrawer(false);
        getOrganizationsList();
        clearFormRecord();
    };

    // 清空记录
    const clearFormRecord = () => {
        setBranchList([]);
        setAffiliatedList([]);
        setFileList([]);
    };

    // 编辑
    const onEdit = () => {
        setOpenAddOrgDrawer(true);
        setOpenViewOrgDrawer(false);
    };

    // 查看医院
    const viewHospital = curId => {
        setCurrentRecordId(curId);
    };

    // 编辑医院
    const editHospital = curId => {
        setCurrentRecordId(curId);
        setOpenAddOrgDrawer(true);
        setOpenViewOrgDrawer(false);
    };

    // 附件下载
    const downloadFile = item => {
        const path = getFileUrl(item.url);
        if (path) {
            fetch(path)
                .then(response => response.blob())
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = item.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                })
                .catch(e => console.error(e));
        } else {
            message.error('文件下载路径不存在');
        }
    };

    // 审核通过
    const handleApprove = async () => {
        try {
            const res = await organizationsReviewedApi({ id });
            if (res && res.status_code === 200) {
                message.success('审核通过成功');
                setIsSwitchStatus(false);
                getOrganizationsInfo();
                getOrganizationsList();

                // 上报操作日志
                try {
                    await logOrganizationApprove(id);
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                    // 日志上报失败不影响主流程
                }
            }
        } catch (error) {
            console.error('审核通过失败', error);
            message.error('审核通过失败');
        }
    };

    // 审核驳回
    const handleReject = async () => {
        try {
            const res = await organizationsRejectedApi({ id });
            if (res && res.status_code === 200) {
                message.success('审核驳回成功');
                setIsSwitchStatus(false);
                getOrganizationsInfo();
                getOrganizationsList();

                // 上报操作日志
                try {
                    await logOrganizationReject(id, '信息无效');
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                    // 日志上报失败不影响主流程
                }
            }
        } catch (error) {
            console.error('审核驳回失败', error);
            message.error('审核驳回失败');
        }
    };

    // 提交审核（保留原有函数，但不再使用）
    const sumitReview = async () => {
        if (reviewStatus) {
            const reqApi = reviewStatus === 1 ? organizationsReviewedApi : organizationsRejectedApi;
            try {
                const res = await reqApi({ id });
                if (res && res.status_code === 200) {
                    message.success('提交审核成功');
                    setIsSwitchStatus(false);
                    getOrganizationsInfo();
                    getOrganizationsList();

                    // 上报操作日志
                    try {
                        if (reviewStatus === 1) {
                            // 审核通过
                            await logOrganizationApprove(id);
                        } else {
                            // 审核驳回 - 这里使用默认的驳回理由，实际应用中可能需要用户输入
                            await logOrganizationReject(id, '信息无效');
                        }
                    } catch (logError) {
                        console.error('操作日志上报失败', logError);
                        // 日志上报失败不影响主流程
                    }
                }
            } catch (error) {
                console.error('审核提交失败', error);
                message.error('提交审核失败');
            }
        } else {
            message.warning('请选择审核状态');
        }
    };

    const onChange = e => {
        setReviewStatus(e.target.value);
    };

    // 获取上传文件 - 前缀
    const getFileUrl = url => {
        if (url && url.includes('https')) {
            return url;
        }
        if (url && !url.includes('https')) {
            return config.OSS_PREFIX + url;
        }
        return null;
    };

    // 处理周年
    const dealYear = year => {
        // 4 或 9 的余数，则展示将满周年
        if (year && year !== '-') {
            if ((parseInt(year) + 1) % 5 === 0) {
                return (
                    <>
                        <Text type="warning">
                            （{year}周年，将满{year + 1}周年）
                        </Text>
                    </>
                );
            } else {
                return (
                    <>
                        <Text type="warning">（{year}周年）</Text>
                    </>
                );
            }
        }
    };

    // 准备机构别称
    const prepareOrgAliases = org => {
        if (!org?.aliases) return '';

        try {
            // 解析JSON格式的别称数据
            const aliases = JSON.parse(org.aliases);
            if (!Array.isArray(aliases) || aliases.length === 0) return '';

            // 分离当前别称和曾用名
            const currentAliases = aliases.filter(alias => !alias.former);
            const formerAliases = aliases.filter(alias => alias.former);

            let aliasText = '';

            // 添加当前别称
            if (currentAliases.length > 0) {
                const currentNames = currentAliases.map(alias => alias.name).join('、');
                aliasText += currentNames;
            }

            // 添加曾用名
            if (formerAliases.length > 0) {
                const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
                if (!!aliasText) aliasText += '、';
                aliasText += formerNames;
            }

            return aliasText;
        } catch (error) {
            console.error('解析机构别称失败:', error);
            return '';
        }
    };

    // 检测文本是否超过指定行数
    const checkIfTextExceedsLines = (text, maxLines = 10) => {
        if (!text || text === '-') return false;

        // 如果introductionRef还没有挂载，返回false
        if (!introductionRef.current) return false;

        // 创建一个临时元素来测量文本行数
        const tempElement = document.createElement('div');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.whiteSpace = 'pre-wrap';
        tempElement.style.wordWrap = 'break-word';
        tempElement.style.lineHeight = '1.5';

        // 获取实际的容器宽度
        const containerWidth = introductionRef.current.offsetWidth;
        tempElement.style.width = `${containerWidth}px`;

        // 获取当前元素的计算样式
        const computedStyle = window.getComputedStyle(introductionRef.current);
        tempElement.style.fontFamily = computedStyle.fontFamily;
        tempElement.style.fontSize = computedStyle.fontSize;
        tempElement.style.fontWeight = computedStyle.fontWeight;

        tempElement.textContent = text;

        document.body.appendChild(tempElement);

        const lineHeight = parseFloat(computedStyle.lineHeight) || 1.5 * parseFloat(computedStyle.fontSize);
        const totalHeight = tempElement.offsetHeight;
        const actualLines = Math.ceil(totalHeight / lineHeight);

        document.body.removeChild(tempElement);

        return actualLines > maxLines;
    };

    // 渲染简介内容（支持展开/收起）
    const renderIntroduction = (introduction, introductionSrc) => {
        if ((!introduction || introduction === '-') && (!introductionSrc || introductionSrc === '-')) return <Text type="secondary">&ndash;</Text>;

        if (introduction && introduction !== '-') {
            return (
                <div className="org-introduction-container">
                    <div ref={introductionRef} className={`org-introduction ${!isIntroductionExpanded ? 'collapsed' : ''}`}>
                        {introduction}
                    </div>
                    {shouldShowToggle && (
                        <Link onClick={() => setIsIntroductionExpanded(!isIntroductionExpanded)} style={{ marginTop: 8, display: 'block' }}>
                            {isIntroductionExpanded ? '收起' : '展开'}
                        </Link>
                    )}
                </div>
            );
        }

        return '';
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const prepareLogoUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    // 撤销合并
    const revokeMerge = async row => {
        try {
            const res = await cancelMergedOrganizationsApi({ id: row.mergedToId ? row.id : id }); // 撤销机构为主机构时 - 传参当前页面id
            if (res && res.status_code === 200) {
                message.success('撤销成功！');
                getOrganizationsInfo();

                // 上报操作日志
                try {
                    await logOrganizationUnmerge(id, row);
                } catch (logError) {
                    console.error('操作日志上报失败', logError);
                    // 日志上报失败不影响主流程
                }
            }
        } catch (error) {
            console.error('撤销合并失败', error);
            message.error('撤销合并失败');
        }
    };

    // 根据id查询机构信息
    const getOrganizationsInfo = useCallback(async () => {
        const res = await organizationsInfoApi({ id });
        if (res && res.status_code === 200 && res.data) {
            const values = res.data;
            setOrgType(values.orgType);

            const newValues = { ...values };

            newValues.logo = prepareLogoUrl(values.logo); // logo
            newValues.bizLicense = !!values.bizLicense ? prepareBCOSSUrl(values.bizLicense) : ''; // 营业执照
            newValues.orgTypeName = values.orgType || values.orgType === 0 ? orgTypeList.find(item => item.value === values.orgType)?.label : ''; // 机构类型
            newValues.area = !!values.province ? `${values.province}${values.city && '/'}${values.city}${values.district && '/'}${values.district}` : <Text type="secondary">&ndash;</Text>; // 所在地区

            // 院区回显
            if (values.branchList) {
                console.log('branchList', values.branchList);
                // 院区排序和分组：
                // 有“登记号（creditCode）”的院区排在前
                // 相同登记号的院区归为一组
                const sortedBranchList = [...values.branchList].sort((a, b) => {
                    // 有登记号的排在前面
                    const aHasCreditCode = !!a.creditCode;
                    const bHasCreditCode = !!b.creditCode;

                    if (aHasCreditCode && !bHasCreditCode) {
                        return -1;
                    }
                    if (!aHasCreditCode && bHasCreditCode) {
                        return 1;
                    }

                    // 如果都有登记号，按登记号分组（相同登记号的排在一起）
                    if (aHasCreditCode && bHasCreditCode) {
                        const creditCodeCompare = (a.creditCode || '').localeCompare(b.creditCode || '');
                        if (creditCodeCompare !== 0) {
                            return creditCodeCompare;
                        }
                        // 相同登记号时，主院区排在前
                        if (a.mainBranchCampus === 1 && b.mainBranchCampus !== 1) {
                            return -1;
                        }
                        if (a.mainBranchCampus !== 1 && b.mainBranchCampus === 1) {
                            return 1;
                        }
                        // 最后按ID排序
                        return a.id - b.id;
                    }

                    // 都没有登记号时，主院区排在前
                    if (a.mainBranchCampus === 1 && b.mainBranchCampus !== 1) {
                        return -1;
                    }
                    if (a.mainBranchCampus !== 1 && b.mainBranchCampus === 1) {
                        return 1;
                    }

                    // 最后按ID排序
                    return a.id - b.id;
                });
                console.log('sortedBranchList', sortedBranchList);

                setBranchList(sortedBranchList);
            }

            // 附属医院回显
            if (values.affiliatedList) {
                setAffiliatedList(values.affiliatedList);
            }

            // 附件回显
            if (values.files) {
                setFileList(JSON.parse(values.files) || []);
            }

            // 被合并机构列表回显
            if (values.mergedList) {
                setMergedList(values.mergedList);
            } else {
                setMergedList([]);
            }

            setReviewStatus(values.reviewStatus);

            console.log('newValues', newValues);

            setInfo({ ...newValues });
        }
    }, [id]);

    const onCopy = msg => {
        copy(msg);
        message.success('已复制到剪贴板');
    };

    useEffect(() => {
        if (openViewOrgDrawer && id) {
            getOrganizationsInfo();
        }
    }, [openViewOrgDrawer, id, getOrganizationsInfo]);

    // 当简介内容或抽屉打开状态变化时，重新检测是否需要展开收起
    useEffect(() => {
        if (openViewOrgDrawer && info.introduction && info.introduction !== '-') {
            const timer = setTimeout(() => {
                const exceedsLines = checkIfTextExceedsLines(info.introduction);
                setShouldShowToggle(exceedsLines);
            }, 150); // 等待DOM渲染完成

            return () => clearTimeout(timer);
        } else {
            setShouldShowToggle(false);
        }
    }, [openViewOrgDrawer, info.introduction]);

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                className="view-organization-drawer"
                title={
                    <>
                        <span style={{ verticalAlign: 'middle' }}>机构详情</span>
                        <Text type="secondary" style={{ verticalAlign: 'middle' }}>
                            #{id}
                        </Text>
                        {info?.mergedToId !== 0 && (
                            <Tag color="#f50" className="merge-tag" style={{ padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                被合并
                            </Tag>
                        )}
                    </>
                }
                open={openViewOrgDrawer}
                size="100%"
                placement="bottom"
                onClose={onClose}
                mask={{ closable: false }}
                footer={
                    <div className="org-footer">
                        <Space>
                            <span>
                                <span>审核状态：</span>
                                <span>{id && info?.reviewStatus !== undefined && <Text type={ReviewStatusMap[info.reviewStatus]?.type}>{ReviewStatusMap[info.reviewStatus]?.text}</Text>}</span>
                            </span>

                            {(info.reviewStatus === 0 || isSwitchStatus) && (
                                <div className="review-status">
                                    <Space>
                                        <Button type="primary" size="small" onClick={() => handleApprove()}>
                                            审核通过
                                        </Button>
                                        <Popconfirm title="确定驳回该机构信息吗？" onConfirm={() => handleReject()} okText="确定" cancelText="取消">
                                            <Button danger size="small">
                                                审核驳回
                                            </Button>
                                        </Popconfirm>
                                        {isSwitchStatus && (
                                            <Button type="link" size="small" onClick={() => setIsSwitchStatus(false)}>
                                                取消
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            )}
                            {!!info.reviewStatus && !isSwitchStatus && (
                                <Button type="link" size="small" onClick={() => setIsSwitchStatus(!isSwitchStatus)}>
                                    重新审核
                                </Button>
                            )}
                        </Space>
                        <Space>
                            <Button onClick={onClose}>取消</Button>
                            {showEditButton && (
                                <Button type="primary" onClick={onEdit}>
                                    编辑
                                </Button>
                            )}
                        </Space>
                    </div>
                }
            >
                <Button
                    type="link"
                    onClick={openRecord}
                    icon={
                        <span className="anticon">
                            <i className="bi bi-arrow-bar-left"></i>
                        </span>
                    }
                    className="org-record-btn"
                >
                    操作记录
                </Button>

                <Row justify="center">
                    <Col md={24} lg={20} xl={18}>
                        <Form labelCol={{ sm: { span: 24 }, md: { span: 6 }, lg: { span: 4 } }} wrapperCol={{ sm: { span: 24 }, md: { span: 18 }, lg: { span: 20 } }}>
                            <div className="module-title">
                                <span>基本信息</span>
                            </div>

                            <Form.Item label="机构名称">
                                {info.name}
                                <Tag color="processing">{info.orgTypeName}</Tag>
                            </Form.Item>

                            <Form.Item label="别称">{!!prepareOrgAliases(info) ? prepareOrgAliases(info) : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="简称">{!!info.shortName ? info.shortName : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="显示名称">{!!info.displayName ? info.displayName : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="Logo">{!!info?.logo ? <Image width={200} src={info.logo} className="share-image-area" alt="机构 Logo" /> : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="简介">
                                <div>{renderIntroduction(info.introduction, info.introductionSrc)}</div>
                                {!!info.introductionSrc && (
                                    <div className="org-introduction-src">
                                        来源：<Link onClick={() => window.open(info.introductionSrc)}>{info.introductionSrc}</Link>
                                    </div>
                                )}
                            </Form.Item>

                            {/* 政府机构无成立时间字段 */}
                            {orgType !== 2 && (
                                <Form.Item label="成立时间">
                                    {!!info.establishedAt && !!info.yearsSinceEstablished ? (
                                        <>
                                            <span>{formatEstablishedTime(info.establishedAt, info.establishedDateType)}</span>
                                            <span>{dealYear(info.yearsSinceEstablished)}</span>
                                        </>
                                    ) : (
                                        <Text type="secondary">&ndash;</Text>
                                    )}
                                </Form.Item>
                            )}

                            {/* 医疗机构类型有以下字段：医疗机构类型、医疗机构性质、医疗机构层级、等级、国家医学中心、国家区域医疗中心、国家级临床重点专科、省级临床重点专科、市级临床重点专科 */}
                            {orgType === 1 && (
                                <>
                                    <Form.Item label="登记号">{!!info.licenseNo ? info.licenseNo : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="医疗机构类型">{!!info.medType ? info.medType : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="医疗机构性质">{!!info.medOwnership ? info.medOwnership : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="医疗机构层级">{!!info.medTier ? info.medTier : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="等级">
                                        {(() => {
                                            const hasLevel = !!info.medLevel && info.medLevel !== '-' && typeof info.medLevel === 'string' && info.medLevel !== '未知';
                                            const hasGrade = !!info.medGrade && info.medGrade !== '-' && typeof info.medGrade === 'string' && info.medGrade !== '未知';

                                            if (hasLevel && hasGrade) {
                                                return `${info.medLevel}${info.medGrade}`;
                                            } else if (hasLevel) {
                                                return info.medLevel;
                                            } else if (hasGrade) {
                                                return info.medGrade;
                                            } else {
                                                return <Text type="secondary">&ndash;</Text>;
                                            }
                                        })()}
                                    </Form.Item>

                                    <Form.Item label="国家医学中心">{info.nationalMedicalCenter === 1 ? '是' : info.nationalMedicalCenter === 0 ? '否' : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="国家区域医疗中心">{info.nationalRegionalMedicalCenter === 1 ? info.nationalRegionalMedicalCenterType === 1 ? '输入医院' : '输出医院' : info.nationalRegionalMedicalCenter === 0 ? '否' : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="国家级临床重点专科">{!!info.nationalKeyClinicalSpecialty ? info.nationalKeyClinicalSpecialty : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="省级临床重点专科">{!!info.provincialKeyClinicalSpecialty ? info.provincialKeyClinicalSpecialty : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="市级临床重点专科">{!!info.municipalKeyClinicalSpecialty ? info.municipalKeyClinicalSpecialty : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="百度百科词条">{!!info.baiduBaikeUrl ? <Link onClick={() => window.open(info.baiduBaikeUrl)}>查看词条</Link> : <Text type="secondary">&ndash;</Text>}</Form.Item>
                                </>
                            )}

                            {/* 医疗机构类型有以下字段：运营信息，床位数、员工数、年门急诊量、院区 */}
                            {orgType === 1 && (
                                <>
                                    <div className="module-title">运营信息</div>

                                    <Form.Item label="床位数">{!!info.bedCount ? info.bedCount : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="员工数">{!!info.employeeCount ? info.employeeCount : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="年门急诊量">{!!info.annualOutpatientEmergVisits ? info.annualOutpatientEmergVisits : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="院区">
                                        {branchList && Array.isArray(branchList) && !!branchList.length ? (
                                            branchList.map(item => (
                                                <div className={`hospital-list-item ${!!item.creditCode ? 'credit-code-item' : ''}`} key={item.id}>
                                                    {!!item.creditCode && (
                                                        <div className="credit-code" style={{ backgroundColor: getCreditCodeBackgroundColor(item.creditCode) }}>
                                                            登记号：{item.creditCode}
                                                        </div>
                                                    )}
                                                    <div className="hospital-list-item-title">
                                                        <span>
                                                            {item.name}
                                                            <Text type="secondary"> #{item.id}</Text>
                                                            {item.mainBranchCampus === 1 && <Tag color="#f50">主院区</Tag>}
                                                        </span>
                                                        <span>{item.address}</span>
                                                    </div>
                                                    <div>
                                                        {item.id === id && <Text type="secondary">当前机构</Text>}
                                                        {item.id !== id && (
                                                            <Space>
                                                                <Link onClick={() => viewHospital(item.id)}>查看</Link>
                                                                <Link onClick={() => editHospital(item.id)}>编辑</Link>
                                                            </Space>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <Text type="secondary">&ndash;</Text>
                                        )}
                                    </Form.Item>
                                </>
                            )}

                            {/* 企业有以下字段：营业执照、统一社会信用代码、法人代表、经营范围 */}
                            {orgType === 5 && (
                                <>
                                    <Form.Item name="bizLicense" label="营业执照" className="logo-image">
                                        {!!info.bizLicense ? <Image width={200} src={info.bizLicense} className="share-image-area" alt="营业执照" /> : <Text type="secondary">&ndash;</Text>}
                                    </Form.Item>

                                    <Form.Item label="统一社会信用代码">{!!info.creditCode ? info.creditCode : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="法人代表">{!!info.legalRep ? info.legalRep : <Text type="secondary">&ndash;</Text>}</Form.Item>

                                    <Form.Item label="经营范围">{!!info.bizScope ? info.bizScope : <Text type="secondary">&ndash;</Text>}</Form.Item>
                                </>
                            )}

                            {/* 科研院校有以下字段：附属医院 */}
                            {orgType === 4 && (
                                <>
                                    <Form.Item label="附属医院">
                                        {affiliatedList && Array.isArray(affiliatedList) && !!affiliatedList.length ? (
                                            affiliatedList.map(item => (
                                                <div className="hospital-list-item" key={item.id}>
                                                    <div className="hospital-list-item-title">
                                                        <span>
                                                            {item.name}
                                                            <Text type="secondary"> #{item.id}</Text>
                                                        </span>
                                                        <span>{item.address}</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <Text type="secondary">&ndash;</Text>
                                        )}
                                    </Form.Item>
                                </>
                            )}

                            <div className="module-title">联系信息</div>

                            <Form.Item label="所在地区">{info.area}</Form.Item>

                            <Form.Item label="地址">{!!info.address ? info.address : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="邮编">{!!info.postCode ? info.postCode : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="传真">{!!info.fax ? info.fax : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="电子邮箱">{!!info.email ? info.email : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="联系电话">{!!info.tel ? info.tel : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <Form.Item label="网址">{!!info?.site ? info.site?.startsWith('http') ? <Link onClick={() => window.open(info.site)}>{info.site}</Link> : info.site : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <div className="module-title">其它信息</div>

                            <Form.Item label="附件">
                                {fileList && Array.isArray(fileList) && !!fileList.length ? (
                                    fileList.map((file, index) => (
                                        <div className="attachment-list-item" key={index}>
                                            <div className="attachment-title">
                                                <span className="anticon">
                                                    <i className="bi bi-file-earmark"></i>
                                                </span>
                                                <span>{file.name}</span>
                                            </div>
                                            <Button type="link" onClick={() => downloadFile(file)}>
                                                下载
                                            </Button>
                                        </div>
                                    ))
                                ) : (
                                    <Text type="secondary">&ndash;</Text>
                                )}
                            </Form.Item>

                            <Form.Item label="备注">{!!info.remarks ? info.remarks : <Text type="secondary">&ndash;</Text>}</Form.Item>

                            <div className="org-sys-info">
                                <Form.Item label="来源">
                                    {info.source === 0 ? (
                                        '后台新增'
                                    ) : (
                                        <Popover
                                            content={
                                                <>
                                                    <span style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onCopy(info.memcard)}>
                                                        {info.memcard}
                                                    </span>
                                                    <small>（点击复制）</small>
                                                </>
                                            }
                                        >
                                            用户新增
                                        </Popover>
                                    )}
                                </Form.Item>
                                <Form.Item label="创建时间">
                                    {!!info?.createdBy && <span style={{ marginRight: 8 }}>{info.createdBy}</span>}
                                    <Text type="secondary">{info.createdAt}</Text>
                                </Form.Item>
                                <Form.Item label="最后更新">
                                    <Text type="secondary">{info.updatedAt}</Text>
                                </Form.Item>
                            </div>
                            {mergedList && Array.isArray(mergedList) && !!mergedList.length && (
                                <div className="org-merge-list">
                                    <Form.Item label="合并信息">
                                        {!info.mergedToId && (
                                            <p>
                                                当前机构为“主机构”，<Text type="danger">下列机构已被本机构合并</Text>，检索被合并机构会返回本机构信息。
                                            </p>
                                        )}
                                        {!!info.mergedToId && (
                                            <p>
                                                <Text type="danger">当前机构已被合并</Text>，下述机构为“主机构”，检索本机构会返回下述机构信息。
                                            </p>
                                        )}
                                        {(showAllMergeList ? mergedList : mergedList.slice(0, 3)).map(item => (
                                            <div className="hospital-list-item" key={item.id}>
                                                <div className="hospital-list-item-title">
                                                    <span>
                                                        {item.name}
                                                        <Text type="secondary"> #{item.id}</Text>
                                                    </span>
                                                    <span>{item.address}</span>
                                                </div>
                                                <Popconfirm title="确定撤销合并？" onConfirm={() => revokeMerge(item)} okText="确定" cancelText="取消">
                                                    <Link>撤销合并</Link>
                                                </Popconfirm>
                                            </div>
                                        ))}
                                        {mergedList && mergedList.length > 3 && (
                                            <div className="org-merge-more">
                                                <Link onClick={() => setShowAllMergeList(!showAllMergeList)}>
                                                    {showAllMergeList ? (
                                                        <>
                                                            <i className="bi bi-chevron-up"></i>
                                                            收起
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-chevron-down"></i>
                                                            展开{mergedList.length}个机构
                                                        </>
                                                    )}
                                                </Link>
                                            </div>
                                        )}
                                    </Form.Item>
                                </div>
                            )}
                        </Form>
                    </Col>
                </Row>
            </Drawer>

            {id && <OperationLogDrawer open={openRecordDrawer} onClose={() => setOpenRecordDrawer(false)} entityType="organization" entityId={id} module="organization" />}
        </>
    );
};

export default OrganizationView;
